// @flow

import * as React from 'react';
import { localize } from '@ta/common.i18n';
import { DeprecatedModalLink } from '@ta/overlays.modal';
import { TabActiveContext } from '@ta/social.tabs';
import { ScrollAffordance } from '@ta/social.scroll';
import type {
  MemberInfo,
  MemberProfile,
  SuggestedFolloweesTypeInput,
} from '@ta/social.member-types';
import { ItemIdContext, InteractionLogger } from '@ta/tracking.interactions';

import ExternalAccountTypeDataProvider from './ExternalAccountTypeDataProvider';
import FolloweeCardDetailed from './FolloweeCardDetailed';
import FolloweeCardSimple from './FolloweeCardSimple';
// eslint-disable-next-line import/no-cycle
import SuggestedMembersModal from './SuggestedMembersModal';

import styles from './FollowShelf.less';

type ObjectWrapper = {
  suggestedFolloweesInfo: MemberInfo,
  object: MemberProfile,
};

type Props = {
  isSingleObject: boolean,
  suggestedFollowees: ObjectWrapper[],
  title?: string,
  shelfId: string,
  hideShowMore?: boolean,
  showBio?: boolean,
  forceSimple?: boolean,
  clusterId?: string,
};

type State = {
  hasTrackedScroll: boolean,
};

class FollowShelf extends React.Component<Props, State> {
  static defaultProps = {
    isSingleObject: false,
    title: undefined,
    hideShowMore: false,
    showBio: false,
    forceSimple: false,
    clusterId: undefined,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasTrackedScroll: false,
    };
  }

  render() {
    const {
      isSingleObject,
      suggestedFollowees,
      title,
      shelfId,
      hideShowMore,
      showBio,
      forceSimple,
      clusterId,
    } = this.props;

    const followeesType: SuggestedFolloweesTypeInput =
      suggestedFollowees &&
      suggestedFollowees.length &&
      suggestedFollowees[0].suggestedFolloweesInfo
        ? suggestedFollowees[0].suggestedFolloweesInfo.suggestedFolloweeType
        : null;

    const featuredTypes = ['FEATURED', 'GREAT_CONTENT_PARTNERS', 'NEW_PARTNERS'];

    // Fit 2 cards across if we have any FEATURED cards, 3 across otherwise.
    const widescreenClassName =
      featuredTypes.includes(followeesType) && !forceSimple ? 'is-6-widescreen' : 'is-4-widescreen';

    let sectionClassName;
    if (clusterId) {
      sectionClassName = styles.clusteredSection;
    }
    // if not in a cluster but is a single object
    else if (isSingleObject) {
      sectionClassName = `${styles.section} ui_card`;
    } else {
      sectionClassName = styles.sections;
    }

    return (
      <div className={sectionClassName}>
        <InteractionLogger>
          {logInteraction => (
            <TabActiveContext.Consumer>
              {tabData => (
                <React.Fragment>
                  <div className={`ui_header ${styles.shelf_header}`}>
                    <div
                      className={
                        isSingleObject && !clusterId
                          ? styles.header_title
                          : styles.header_title_smaller
                      }
                    >
                      {/* Who to follow on TripAdvisor */}
                      {title || localize('social_home_right_rail_follow_title')}
                    </div>
                    {!hideShowMore ? (
                      <ExternalAccountTypeDataProvider>
                        {profile => (
                          <React.Fragment>
                            <DeprecatedModalLink
                              name={`FollowShelfSeeMore-${shelfId}`}
                              username={profile.username}
                              href={null}
                              className={styles.header_show_more}
                              onClick={e => logInteraction(e, 'seeAll')}
                            >
                              <span className="ui_link">
                                {/* See more */}
                                {localize('followee_shelf_see_more')}
                              </span>
                            </DeprecatedModalLink>
                            <SuggestedMembersModal
                              type={
                                followeesType && followeesType === 'FACEBOOK_FRIENDS'
                                  ? 'Facebook'
                                  : 'TripAdvisor'
                              }
                              modalName={`FollowShelfSeeMore-${shelfId}`}
                              followeesTypes={
                                followeesType && followeesType !== 'FACEBOOK_FRIENDS'
                                  ? [followeesType]
                                  : undefined
                              }
                              placement="SHOW_MORE_FROM_SHELVES"
                            />
                          </React.Fragment>
                        )}
                      </ExternalAccountTypeDataProvider>
                    ) : null}
                  </div>
                  <ScrollAffordance
                    top={false}
                    bottom={false}
                    threshold={100}
                    minDistance={50}
                    snapToEdge={{ left: 0, top: 0, bottom: 0, right: 100 }}
                    // Adjust for Shelf padding. (arrows will be hidden on mobile)
                    shift={{ right: { top: -18 }, left: { top: -18 } }}
                    focus={isSingleObject ? undefined : { left: -12, top: 0 }}
                    className={`${styles.follow_candidates} ui_columns`}
                    isActive={tabData ? tabData.isActive : true}
                    onScroll={
                      this.state.hasTrackedScroll
                        ? undefined
                        : e => {
                            this.setState({ hasTrackedScroll: true });
                            logInteraction(e, 'shelfScroll');
                          }
                    }
                  >
                    {setChild =>
                      suggestedFollowees.map(memberProfile => (
                        <div
                          key={memberProfile.object.userId}
                          ref={setChild(memberProfile.object.userId)}
                          className={
                            isSingleObject
                              ? `ui_column is-4 is-6-desktop ${widescreenClassName}`
                              : `ui_column is-4 is-4-desktop ${widescreenClassName}`
                          }
                        >
                          <ItemIdContext.Provider value={{ itemId: memberProfile.object.userId }}>
                            <div className="ui_card">
                              {!forceSimple &&
                              memberProfile.suggestedFolloweesInfo &&
                              featuredTypes.includes(
                                memberProfile.suggestedFolloweesInfo.suggestedFolloweeType,
                              ) ? (
                                <FolloweeCardDetailed member={memberProfile.object} />
                              ) : (
                                <FolloweeCardSimple
                                  member={memberProfile.object}
                                  suggestedFolloweesInfo={memberProfile.suggestedFolloweesInfo}
                                  showBio={showBio}
                                />
                              )}
                            </div>
                          </ItemIdContext.Provider>
                        </div>
                      ))
                    }
                  </ScrollAffordance>
                </React.Fragment>
              )}
            </TabActiveContext.Consumer>
          )}
        </InteractionLogger>
      </div>
    );
  }
}

export default FollowShelf;
